import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { DocumentStatusEnum, ListFormat, ListSorting, MembersAndGroups, OrderByEnum, OrganizationMember, User } from '@app/models';
import { AppService, ErrorHandlerService, OrganizationService, UserService, WhitelabelService } from '@app/services';
import { DocumentsOrganizationMemberListComponent } from '../../documents-organization-member-list/documents-organization-member-list.component';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { OrganizationMembersPageFragment } from 'src/generated/graphql.default';

@Component({
  selector: 'app-documents-taxonomy',
  templateUrl: './documents-taxonomy.component.html',
  styleUrls: ['./documents-taxonomy.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class DocumentsTaxonomyComponent implements OnInit, OnDestroy {
  @Input() disableMainButton = false;
  @Input() listPath: 'default' | 'group' | 'member' | 'organization';
  @Input() isSearchOpen = false;
  @Output() statusChange = new EventEmitter();
  @Output() formatChange = new EventEmitter();
  @Output() sortingChange = new EventEmitter<void>();
  @Output() newFolderButtonClick = new EventEmitter();
  isOnFoldersListing: boolean;
  isOnTrash: boolean;
  currentUser: User;
  isLoading = false;
  search = '';
  lastSearch: string;
  organizationThings: Array<MembersAndGroups> = [];
  documentStatusFilters = [DocumentStatusEnum.Pending, DocumentStatusEnum.Signed, DocumentStatusEnum.NotSigned, DocumentStatusEnum.Rejected];
  readonly ListFormat = ListFormat;
  readonly OrderByEnum = OrderByEnum;
  readonly listSortings = {
    [ListSorting.CreatedAt]: this.translateService.instant('documents.creationDate'),
    [ListSorting.Name]: this.translateService.instant('documents.name'),
    [ListSorting.UpdatedAt]: this.translateService.instant('documents.lastModified')
  };

  constructor(
    public appService: AppService,
    public whitelabelService: WhitelabelService,
    private route: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private translateService: TranslateService
  ) {
    this.isOnFoldersListing = !!location.pathname.match(/\/pastas|\/f\//);
    this.isOnTrash = !!location.pathname.match('^/documentos/lixeira');

    if (this.route.snapshot.component === DocumentsOrganizationMemberListComponent) {
      this.documentStatusFilters.push(DocumentStatusEnum.Deleted);
    } else {
      const deletedStatusIndex = this.documentStatusFilters.findIndex(status => status === DocumentStatusEnum.Deleted);
      if (deletedStatusIndex >= 0) {
        this.documentStatusFilters.splice(deletedStatusIndex, 1);
      }
      if (this.appService.documentStatusFilter === DocumentStatusEnum.Deleted) {
        this.changeFilter(null);
      }
    }

    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        filter(user => !this.currentUser || this.currentUser.organization.id !== user.organization.id),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          const hasOrganizationChanged = this.currentUser && this.currentUser.organization.id !== user.organization.id;
          this.currentUser = user;
          if (this.isOnFoldersListing || this.canShowDocuments()) {
            this.searchRecords(this.lastSearch, hasOrganizationChanged);
          }
        }
      });
  }

  ngOnInit() {
    this.onResize();
  }

  ngOnDestroy() {}

  searchRecords(search: string = '', ignoreCache?: boolean) {
    if (!this.isLoading && (this.organizationThings.length === 0 || search !== this.lastSearch || ignoreCache)) {
      this.isLoading = true;
      this.isOnFoldersListing ? this.searchOrganizationMembers(search, ignoreCache) : this.searchOrganizationMembersAndGroups(search, ignoreCache);
    }
  }

  searchOrganizationMembers(search: string = '', ignoreCache?: boolean) {
    this.organizationService
      .organizationMembers({ limit: 8, page: 1, search }, { fetchPolicy: ignoreCache ? 'network-only' : 'cache-first' })
      .pipe(
        tap(() => (this.lastSearch = search)),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: page => (this.organizationThings = page.data.map(data => ({ name: data.user.name, id: data.user.id, type: 'member' }))),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  searchOrganizationMembersAndGroups(search: string = '', ignoreCache?: boolean) {
    this.organizationService
      .organizationMembersAndGroups({ limit: 8, page: 1, search }, { ignoreCache })
      .pipe(
        tap(() => (this.lastSearch = search)),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: page => (this.organizationThings = page.data.filter(data => !!data.name)),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  changeSorting(sortingKey: string) {
    this.appService.listSorting = sortingKey as ListSorting;
    this.sortingChange.emit();
  }

  changeDirection(direction: string) {
    this.appService.listDirection = direction as OrderByEnum;
    this.sortingChange.emit();
  }

  changeFilter(statusFilter: DocumentStatusEnum) {
    this.appService.documentStatusFilter = statusFilter;
    this.sortingChange.emit();
  }

  recordPath(item: MembersAndGroups) {
    const mainPath = this.isOnFoldersListing ? 'pastas' : 'documentos';
    const secondaryPath = item.type === 'member' ? 'organizacao' : 'grupo';

    const completePath = `/${mainPath}/${secondaryPath}`;
    return [completePath, item.id];
  }

  canShowDocuments(item?: MembersAndGroups) {
    return (
      this.currentUser &&
      (item
        ? item.type === 'member'
          ? this.currentUser.currentPermissions.view_member_documents_oz
          : this.currentUser.currentPermissions.view_group_documents_oz
        : this.currentUser.currentPermissions.view_member_documents_oz || this.currentUser.currentPermissions.view_group_documents_oz)
    );
  }

  changeFormatList(format: string) {
    this.appService.listFormat = format as ListFormat;

    this.formatChange.emit({ format });
  }

  @HostListener('window:resize') private onResize() {
    if (window.innerWidth <= 992 && this.appService.listFormat !== ListFormat.Box) {
      this.appService.listFormat = ListFormat.Box;
    }
  }
}
